import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3500';
import {
  doGetProjectDeliverableChecklist,
  luHandleAppiError,
  type U3500DeliverableChecklistResponse,
} from '../U3500Service';

export interface UseU3500DeliverableChecklistHandlersParams {
  onSuccess?: (checklist: U3500DeliverableChecklistResponse) => void;
  onError?: (error: unknown) => void;
}

/**
 * Handler : checklist livrables projet (GET U3500/projects/:id/deliverables/checklist).
 */
export function useU3500DeliverableChecklistHandlers(
  params: UseU3500DeliverableChecklistHandlersParams = {}
) {
  const { onSuccess, onError } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadChecklist = useCallback(
    async (projectId: string): Promise<U3500DeliverableChecklistResponse | null> => {
      if (!projectId?.trim()) {
        return null;
      }
      try {
        const checklist = await doGetProjectDeliverableChecklist(axios, projectId.trim());
        onSuccess?.(checklist);
        return checklist;
      } catch (error: unknown) {
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (err.genericErrors?.length) {
          err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        }
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onError, t, toast]
  );

  return { loadChecklist };
}
